import React from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { CC_PANEL } from "@/components/case-checking/caseCheckingChrome";
import {
  buildCaseContextSummary,
  getAdviceScopeReasoning,
  reviewProfileLabel,
  type CaseContextDisplayInput,
} from "@/components/case-checking/caseReviewContext";

export interface CaseContextBarProps extends CaseContextDisplayInput {
  profileLabelBySlug?: ReadonlyMap<string, string>;
  className?: string;
  compact?: boolean;
  /** Omit the clients row when the page title already shows who is advised. */
  hideClients?: boolean;
  /** Human-readable case type for the one-line headline (e.g. Protection). */
  caseTypeLabel?: string | null;
  /**
   * `grid` — labelled rows (list/footer).
   * `headline` — single line: case type · review stage (case detail chrome).
   */
  variant?: "grid" | "headline";
  onAdjudication?: () => void;
  /** Extra actions after adjudication / scope note (e.g. delete). */
  trailingActions?: React.ReactNode;
}

/** Read-only summary: who is advised, review stage, advice type; scope reasoning in tooltip. */
export function CaseContextBar({
  profileLabelBySlug,
  className,
  compact = false,
  hideClients = false,
  caseTypeLabel,
  variant = "grid",
  onAdjudication,
  trailingActions,
  ...input
}: CaseContextBarProps) {
  const rows = buildCaseContextSummary(input, profileLabelBySlug).filter(
    (row) => !(hideClients && row.term === "Clients"),
  );
  const scopeNote = getAdviceScopeReasoning(input);
  const reviewStage =
    rows.find((r) => r.term === "Review stage")?.detail ??
    reviewProfileLabel(input.reviewProfile, profileLabelBySlug);
  const headline = [caseTypeLabel?.trim(), reviewStage].filter(Boolean).join(" · ");

  const chromeActionClass =
    "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-white/10 hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-slate-100";

  if (variant === "headline") {
    if (!headline && !scopeNote && !onAdjudication && !trailingActions) return null;
    return (
      <div
        className={clsx(CC_PANEL, "px-3 py-2.5 flex items-center justify-between gap-3", className)}
        data-testid="case-context-bar"
      >
        {headline ? (
          <p className="text-sm text-slate-700 dark:text-slate-200 min-w-0">{headline}</p>
        ) : (
          <span className="flex-1" />
        )}
        <div className="flex items-center gap-2 shrink-0">
          {onAdjudication ? (
            <Button
              plain
              size="unstyled"
              type="button"
              className={clsx(chromeActionClass, "")}
              onClick={onAdjudication}
              data-testid="case-adjudication-open"
            >
              Adjudication
            </Button>
          ) : null}
          {scopeNote ? (
            <Tooltip
              content={
                <p className="text-sm leading-relaxed whitespace-pre-wrap max-w-sm">{scopeNote}</p>
              }
              placement="bottom"
              maxWidth="24rem"
              clickOnly
            >
              <Button
                plain
                size="unstyled"
                type="button"
                className={clsx(chromeActionClass, "")}
                data-testid="case-context-scope-note"
                aria-label="View advice scope note"
              >
                <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" />
                Scope note
              </Button>
            </Tooltip>
          ) : null}
          {trailingActions}
        </div>
      </div>
    );
  }

  if (rows.length === 0 && !scopeNote) return null;

  return (
    <div
      className={clsx(CC_PANEL, compact ? "px-3 py-2" : undefined, className)}
      data-testid="case-context-bar"
    >
      <div className="flex flex-wrap items-start gap-3">
        {rows.length > 0 ? (
          <dl
            className={clsx(
              "grid flex-1 min-w-0 gap-x-6 gap-y-2 text-sm",
              compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
            )}
          >
            {rows.map(({ term, detail }) => (
              <div key={term} className="min-w-0">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {term}
                </dt>
                <dd className="text-slate-900 dark:text-slate-100 break-words">{detail}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {scopeNote ? (
          <Tooltip
            content={
              <p className="text-sm leading-relaxed whitespace-pre-wrap max-w-sm">{scopeNote}</p>
            }
            placement="bottom"
            maxWidth="24rem"
            clickOnly
          >
            <Button
              plain
              size="unstyled"
              type="button"
              className={clsx(
                chromeActionClass,
                "",
              )}
              data-testid="case-context-scope-note"
              aria-label="View advice scope note"
            >
              <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" />
              Scope note
            </Button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}
